import { createTheme, type ThemeOptions } from '@mui/material/styles';

// Extend the theme interface to include our custom chart colors
declare module '@mui/material/styles' {
  interface Theme {
    chart: {
      primary: {
        background: string;
        border: string;
        hover: string;
        selected: string;
        stroke: string;
        fill: string;
      };
      success: {
        background: string;
        border: string;
      };
      info: {
        background: string;
        border: string;
      };
      loading: {
        overlay: string;
      };
      grid: string;
      border: string;
      white: {
        overlay: string;
        text: string;
      };
    };
  }

  interface ThemeOptions {
    chart?: {
      primary?: {
        background?: string;
        border?: string;
        hover?: string;
        selected?: string;
        stroke?: string;
        fill?: string;
      };
      success?: {
        background?: string;
        border?: string;
      };
      info?: {
        background?: string;
        border?: string;
      };
      loading?: {
        overlay?: string;
      };
      grid?: string;
      border?: string;
      white?: {
        overlay?: string;
        text?: string;
      };
    };
  }
}

export const greyPalette = {
  lightest: '#fafafa',
  lighter: '#f5f5f5',
  light: '#eeeeee',
  medium: '#bdbdbd',
  dark: '#757575',
  darker: '#424242',
  darkest: '#212121',
} as const;

export const brandColors = {
  primary: {
    light: '#667eea',
    main: '#4f46e5',
    dark: '#3b328a',
  },
  secondary: {
    light: '#f59e0b',
    main: '#d97706',
    dark: '#92400e',
  },
  success: {
    light: '#10b981',
    main: '#059669',
    dark: '#047857',
  },
  error: {
    light: '#f87171',
    main: '#ef4444',
    dark: '#dc2626',
  },
  warning: {
    light: '#fbbf24',
    main: '#f59e0b',
    dark: '#d97706',
  },
  info: {
    light: '#60a5fa',
    main: '#3b82f6',
    dark: '#2563eb',
  },
  grey: greyPalette,
  chart: {
    primary: {
      background: '#4f46e519', // primary.main with 10% opacity
      border: '#4f46e533', // primary.main with 20% opacity
      hover: '#4f46e519', // primary.main with 10% opacity
      selected: '#4f46e533', // primary.main with 20% opacity
      stroke: '#4f46e599', // primary.main with 60% opacity
      fill: '#4f46e519', // primary.main with 10% opacity
    },
    success: {
      background: '#10b98119', // success.light with 10% opacity
      border: '#10b98133', // success.light with 20% opacity
    },
    info: {
      background: '#3b82f619', // info.main with 10% opacity
      border: '#3b82f633', // info.main with 20% opacity
    },
    loading: {
      overlay: '#ffffffcc', // white with 80% opacity
    },
    grid: '#0000001a', // black with 10% opacity
    border: '#0000001a', // black with 10% opacity
    white: {
      overlay: '#fffffff2', // white with 95% opacity
      text: '#ffffffcc', // white with 80% opacity
    },
  },
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primary.main,
      light: brandColors.primary.light,
      dark: brandColors.primary.dark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColors.secondary.main,
      light: brandColors.secondary.light,
      dark: brandColors.secondary.dark,
      contrastText: '#ffffff',
    },
    success: {
      main: brandColors.success.main,
      light: brandColors.success.light,
      dark: brandColors.success.dark,
      contrastText: '#ffffff',
    },
    error: {
      main: brandColors.error.main,
      light: brandColors.error.light,
      dark: brandColors.error.dark,
      contrastText: '#ffffff',
    },
    warning: {
      main: brandColors.warning.main,
      light: brandColors.warning.light,
      dark: brandColors.warning.dark,
      contrastText: '#000000',
    },
    info: {
      main: brandColors.info.main,
      light: brandColors.info.light,
      dark: brandColors.info.dark,
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
      disabled: brandColors.grey.medium,
    },
    divider: '#e2e8f0',
  },
  chart: {
    primary: {
      background: brandColors.chart.primary.background,
      border: brandColors.chart.primary.border,
      hover: brandColors.chart.primary.hover,
      selected: brandColors.chart.primary.selected,
      stroke: brandColors.chart.primary.stroke,
      fill: brandColors.chart.primary.fill,
    },
    success: {
      background: brandColors.chart.success.background,
      border: brandColors.chart.success.border,
    },
    info: {
      background: brandColors.chart.info.background,
      border: brandColors.chart.info.border,
    },
    loading: {
      overlay: brandColors.chart.loading.overlay,
    },
    grid: brandColors.chart.grid,
    border: brandColors.chart.border,
    white: {
      overlay: brandColors.chart.white.overlay,
      text: brandColors.chart.white.text,
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
};

export const muiTheme = createTheme(themeOptions);
